import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { 
  saveTrainingSample, 
  getTrainingSamples, 
  generateParsingTemplate, 
  saveParsingTemplate,
  getParsingTemplate,
  testTemplate,
  type ParsingTemplate 
} from '@/lib/vendorInvoiceParsing';
import { InvoiceFieldEditor, type InvoiceData } from './InvoiceFieldEditor';
import { ParsingTemplateBuilder } from './ParsingTemplateBuilder';
import { VendorInvoiceSummaryDisplay, type OverallStats } from './VendorInvoiceSummaryDisplay';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight } from 'lucide-react';
import { 
  Upload, X, FileText, Loader2, Image as ImageIcon, Trash2, CheckCircle2, Eye, FileSpreadsheet,
  RefreshCw, AlertCircle, Layers, Sparkles, TestTube, Info, ChevronLeft, ChevronRight
} from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

interface COGSVendorTrainingStep3MergedProps {
  onNext: () => void;
  onPrevious: () => void;
  selectedVendors: string[];
  currentVendorIndex: number;
  onVendorIndexChange: (index: number) => void;
  editMappingParams?: { vendorId: string; clusterId?: string };
  onEditMappingCleared?: () => void;
  onProcessingStateChange?: (isProcessing: boolean, hasPending: boolean) => void;
}

interface VendorInvoice {
  id: string;
  vendor_id: string;
  invoice_file_name: string;
  invoice_file_url: string;
  ocr_status: string;
  created_at?: string;
  total_amount?: number;
  invoice_date?: string;
  invoice_number?: string;
  line_items?: any[];
  previewUrl?: string | null;
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

const ALLOWED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.csv', '.xlsx', '.xls'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const WARN_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const COGSVendorTrainingStep3Merged = ({
  onNext,
  onPrevious,
  selectedVendors,
  currentVendorIndex,
  onVendorIndexChange,
  editMappingParams,
  onEditMappingCleared,
  onProcessingStateChange,
}: COGSVendorTrainingStep3MergedProps) => {
  const { toast } = useToast();
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorInvoices, setVendorInvoices] = useState<Record<string, VendorInvoice[]>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, Record<string, UploadProgress>>>({});
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  
  // OCR Processing state
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<Record<string, { processed: number; total: number; clusters: number; failed: number }>>({});
  const [failedInvoices, setFailedInvoices] = useState<Record<string, string[]>>({});
  const [hasCheckedInitialStatus, setHasCheckedInitialStatus] = useState(false);
  const [autoProcessingStarted, setAutoProcessingStarted] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [vendorNames, setVendorNames] = useState<Record<string, string>>({});
  
  // Invoice correction state
  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isTemplateBuilderOpen, setIsTemplateBuilderOpen] = useState(false);
  const [trainingSamples, setTrainingSamples] = useState<any[]>([]);
  const [parsingTemplate, setParsingTemplate] = useState<ParsingTemplate | null>(null);
  const [templateTestResults, setTemplateTestResults] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'upload' | 'processing' | 'correction'>('upload');
  const [reviewSubTab, setReviewSubTab] = useState<'invoices' | 'template' | 'testing'>('invoices');
  
  // Preview modal state
  const [previewInvoice, setPreviewInvoice] = useState<VendorInvoice | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // Processing completion and summary state
  const [allProcessingComplete, setAllProcessingComplete] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [processingStats, setProcessingStats] = useState<OverallStats | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState<{ headers: string[]; rows: any[][] } | null>(null);
  
  // Invoice detail modal for OCR results
  const [detailInvoice, setDetailInvoice] = useState<VendorInvoice | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const currentVendorId = selectedVendors[currentVendorIndex];
  const currentVendor = vendors.find(v => v.id === currentVendorId);
  const currentVendorInvoices = vendorInvoices[currentVendorId] || [];
  const invoiceCount = currentVendorInvoices.length;
  const needsInvoices = invoiceCount < 5;
  const hasEnoughInvoices = invoiceCount >= 5;

  useEffect(() => {
    if (selectedVendors.length > 0) {
      loadVendors();
      loadVendorNames();
      loadInvoices();
    }
  }, [selectedVendors]);

  useEffect(() => {
    if (currentVendorId) {
      loadInvoices();
      loadTrainingSamples();
      checkProcessingStatus();
    }
  }, [currentVendorId]);

  useEffect(() => {
    if (currentVendorId && currentVendorInvoices.length > 0) {
      loadParsingTemplate();
    }
  }, [currentVendorId, currentVendorInvoices.length]);

  // Reset vendor-specific state when switching vendors
  useEffect(() => {
    // Reset to Upload tab
    setActiveTab('upload');
    
    // Reset processing state flags for new vendor
    setAutoProcessingStarted(false);
    setHasCheckedInitialStatus(false);
    setAllProcessingComplete(false);
    
    // Reset other vendor-specific state
    setProcessingStats(null);
    setTemplateTestResults([]);
    setReviewSubTab('invoices');
    
    // Clear any invoice selection
    setSelectedInvoice(null);
    setIsEditorOpen(false);
    setDetailInvoice(null);
    setIsDetailModalOpen(false);
    
  }, [currentVendorIndex]);

  // Ensure file input ref is set after vendor change
  useEffect(() => {
    if (currentVendorId) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const input = document.getElementById(`upload-${currentVendorId}`) as HTMLInputElement;
        if (input && !fileInputRefs.current[`upload-${currentVendorId}`]) {
          fileInputRefs.current[`upload-${currentVendorId}`] = input;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentVendorId, activeTab]);

  // Auto-start processing when invoices are uploaded
  useEffect(() => {
    const autoStartProcessing = async () => {
      if (!hasCheckedInitialStatus || autoProcessingStarted || processing) return;
      
      const hasPendingInvoices = currentVendorInvoices.some(inv => inv.ocr_status === 'pending');
      
      if (hasPendingInvoices) {
        setAutoProcessingStarted(true);
        // Switch to processing tab when processing starts
        setActiveTab('processing');
        await handleProcessInvoices();
      }
    };
    
    autoStartProcessing();
  }, [hasCheckedInitialStatus, currentVendorInvoices, autoProcessingStarted, processing]);

  // Notify parent component of processing state
  useEffect(() => {
    if (onProcessingStateChange) {
      const hasPending = currentVendorInvoices.some(inv => inv.ocr_status === 'pending');
      onProcessingStateChange(processing, hasPending);
    }
  }, [processing, currentVendorInvoices, onProcessingStateChange]);

  // Set up polling when processing
  useEffect(() => {
    if (processing) {
      // Switch to processing tab when processing starts
      setActiveTab('processing');
      
      pollingIntervalRef.current = setInterval(async () => {
        await checkProcessingStatus();
        
        const allComplete = Object.values(progress).every(
          p => (p.processed + p.failed) === p.total && p.total > 0
        );
        const hasPending = currentVendorInvoices.some(inv => inv.ocr_status === 'pending');
        
        if (allComplete && !hasPending) {
          setProcessing(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Load stats and show summary when processing completes
          if (currentVendorInvoices.some(inv => inv.ocr_status === 'completed')) {
            setAllProcessingComplete(true);
            await loadProcessingStats();
            setActiveTab('correction');
          }
          toast({
            title: 'Processing Complete',
            description: 'All invoices have been processed. Review the summary below.',
          });
        }
      }, 3000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [processing, progress, currentVendorInvoices]);

  const loadVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .in('id', selectedVendors);

      if (error) throw error;
      setVendors(data || []);
    } catch (error: any) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadVendorNames = async () => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name')
        .in('id', selectedVendors);

      if (error) throw error;
      
      const names: Record<string, string> = {};
      (data || []).forEach((v: any) => {
        names[v.id] = v.name;
      });
      setVendorNames(names);
    } catch (error) {
      console.error('Error loading vendor names:', error);
    }
  };

  // Load statistics for processed invoices
  const loadProcessingStats = async () => {
    try {
      setLoadingStats(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoadingStats(false);
        return;
      }

      // Load vendor names
      const { data: vendorsData } = await supabase
        .from('vendors')
        .select('id, name')
        .in('id', selectedVendors);

      const vendorMap = new Map((vendorsData || []).map((v: any) => [v.id, v.name]));

      // Load processed invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('vendor_invoice_training')
        .select('id, vendor_id, total_amount, line_items, ocr_status')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .eq('ocr_status', 'completed');

      if (invoicesError) throw invoicesError;

      // Load clusters
      const { data: clustersData, error: clustersError } = await supabase
        .from('invoice_line_item_clusters')
        .select('id, vendor_id, cluster_name, raw_line_items')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors);

      if (clustersError) throw clustersError;

      // Calculate stats per vendor
      interface VendorStats {
        vendor_id: string;
        vendor_name: string;
        invoices_processed: number;
        clusters_identified: number;
        total_line_items: number;
        total_amount: number;
      }

      const vendorStatsMap = new Map<string, VendorStats>();

      selectedVendors.forEach(vendorId => {
        const vendorName = vendorMap.get(vendorId) || 'Unknown Vendor';
        const vendorInvoices = (invoicesData || []).filter(inv => inv.vendor_id === vendorId);
        const vendorClusters = (clustersData || []).filter(cl => cl.vendor_id === vendorId);

        let totalLineItems = 0;
        let totalAmount = 0;

        vendorInvoices.forEach(invoice => {
          if (invoice.line_items && Array.isArray(invoice.line_items)) {
            totalLineItems += invoice.line_items.length;
          }
          if (invoice.total_amount) {
            totalAmount += parseFloat(invoice.total_amount.toString());
          }
        });

        vendorStatsMap.set(vendorId, {
          vendor_id: vendorId,
          vendor_name: vendorName,
          invoices_processed: vendorInvoices.length,
          clusters_identified: vendorClusters.length,
          total_line_items: totalLineItems,
          total_amount: totalAmount,
        });
      });

      const vendorStats = Array.from(vendorStatsMap.values());

      // Calculate overall stats
      const overallStats: OverallStats = {
        totalVendors: vendorStats.length,
        totalInvoices: vendorStats.reduce((sum, v) => sum + v.invoices_processed, 0),
        totalClusters: vendorStats.reduce((sum, v) => sum + v.clusters_identified, 0),
        totalLineItems: vendorStats.reduce((sum, v) => sum + v.total_line_items, 0),
        totalAmount: vendorStats.reduce((sum, v) => sum + v.total_amount, 0),
        vendorStats,
      };

      setProcessingStats(overallStats);
    } catch (error: any) {
      console.error('Error loading stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice statistics',
        variant: 'destructive',
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const loadInvoices = async () => {
    try {
      if (selectedVendors.length === 0) {
        setVendorInvoices({});
        return;
      }

      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('vendor_invoice_training')
        .select('*')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const invoicesByVendor: Record<string, VendorInvoice[]> = {};
      (data || []).forEach((inv: any) => {
        if (!invoicesByVendor[inv.vendor_id]) {
          invoicesByVendor[inv.vendor_id] = [];
        }
        invoicesByVendor[inv.vendor_id].push(inv);
      });

      setVendorInvoices(invoicesByVendor);
    } catch (error: any) {
      console.error('Error loading invoices:', error);
      setVendorInvoices({});
    }
  };

  const checkProcessingStatus = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('vendor_invoice_training')
        .select('id, vendor_id, ocr_status, invoice_file_name, invoice_file_url, created_at, total_amount, invoice_date, invoice_number, line_items')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Store full invoice details for display
      const invoicesByVendor: Record<string, VendorInvoice[]> = {};
      (data || []).forEach((inv: any) => {
        if (!invoicesByVendor[inv.vendor_id]) {
          invoicesByVendor[inv.vendor_id] = [];
        }
        invoicesByVendor[inv.vendor_id].push(inv);
      });

      const status: Record<string, { processed: number; total: number; clusters: number; failed: number }> = {};
      const failed: Record<string, string[]> = {};

      (data || []).forEach((inv: any) => {
        if (!status[inv.vendor_id]) {
          status[inv.vendor_id] = { processed: 0, total: 0, clusters: 0, failed: 0 };
          failed[inv.vendor_id] = [];
        }
        status[inv.vendor_id].total++;
        if (inv.ocr_status === 'completed') {
          status[inv.vendor_id].processed++;
        } else if (inv.ocr_status === 'failed') {
          status[inv.vendor_id].failed++;
          failed[inv.vendor_id].push(inv.invoice_file_name || inv.id);
        }
      });

      // Get cluster counts
      const { data: clusters } = await supabase
        .from('invoice_line_item_clusters')
        .select('vendor_id')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors);

      if (clusters) {
        clusters.forEach((c: any) => {
          if (status[c.vendor_id]) {
            status[c.vendor_id].clusters++;
          }
        });
      }

      setProgress(status);
      setFailedInvoices(failed);
      setHasCheckedInitialStatus(true);
      
      // Update vendor invoices with processing data
      setVendorInvoices(prev => ({ ...prev, ...invoicesByVendor }));
    } catch (error: any) {
      console.error('Error checking status:', error);
      setHasCheckedInitialStatus(true);
    }
  };

  const handleProcessInvoices = async () => {
    try {
      setProcessing(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const { data: pendingInvoices, error: fetchError } = await supabase
        .from('vendor_invoice_training')
        .select('id, vendor_id')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .eq('ocr_status', 'pending');

      if (fetchError) throw fetchError;

      if (!pendingInvoices || pendingInvoices.length === 0) {
        setProcessing(false);
        await checkProcessingStatus();
        return;
      }

      const invoiceIds = pendingInvoices.map(inv => inv.id);

      toast({
        title: 'Processing Started',
        description: `Processing ${invoiceIds.length} invoice(s). This may take a few minutes.`,
      });

      const { data, error } = await supabase.functions.invoke('process-vendor-invoice', {
        body: {
          invoice_ids: invoiceIds,
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to process invoices');
      }

      const vendorIds = [...new Set(pendingInvoices.map(inv => inv.vendor_id))];
      for (const vendorId of vendorIds) {
        await supabase
          .from('vendor_training_progress')
          .update({
            invoices_processed: pendingInvoices.filter(inv => inv.vendor_id === vendorId).length,
            status: 'processing',
          })
          .eq('company_id', companyId)
          .eq('vendor_id', vendorId);
      }

      await triggerLineItemGrouping(companyId, vendorIds);

      toast({
        title: 'Processing Complete',
        description: `Processed ${data.processed || invoiceIds.length} invoice(s)${data.failed > 0 ? `, ${data.failed} failed` : ''}.`,
      });

      await checkProcessingStatus();
    } catch (error: any) {
      console.error('Error processing invoices:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start processing',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const triggerLineItemGrouping = async (companyId: string, vendorIds: string[]) => {
    try {
      const { groupInvoiceLineItems } = await import('@/lib/vendorInvoiceOCR');
      for (const vendorId of vendorIds) {
        await groupInvoiceLineItems(vendorId, companyId);
      }
    } catch (error) {
      console.error('Error triggering line item grouping:', error);
    }
  };

  // File upload functions (from Step 2)
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isAllowedExtension = ALLOWED_FILE_TYPES.includes(fileExtension);
    const isAllowedMimeType = ALLOWED_MIME_TYPES.includes(file.type) || 
      file.type.startsWith('image/') || 
      file.type === '' || 
      file.type === 'application/octet-stream';

    if (!isAllowedExtension && !isAllowedMimeType) {
      return {
        valid: false,
        error: `File type ${fileExtension} (${file.type || 'unknown'}) not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(', ')}`,
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum ${MAX_FILE_SIZE / 1024 / 1024}MB`,
      };
    }

    if (file.size > WARN_FILE_SIZE) {
      toast({
        title: 'Large file',
        description: `File ${file.name} is ${(file.size / 1024 / 1024).toFixed(2)}MB. Upload may take longer.`,
        variant: 'default',
      });
    }

    return { valid: true };
  };

  // Parse CSV/Excel files (simplified version - full implementation from Step 2)
  const parseInvoiceFile = async (file: File): Promise<{ invoiceNumber?: string; invoiceDate?: string; totalAmount?: number; lineItems?: any[] }> => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (extension === 'csv') {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          dynamicTyping: false,
          complete: (results) => {
            try {
              const allRows = results.data as any[][];
              if (allRows.length === 0) {
                resolve({});
                return;
              }
              // Simplified parsing - full implementation would be here
              resolve({});
            } catch (error) {
              reject(error);
            }
          },
          error: (error) => reject(error),
        });
      });
    } else if (extension === 'xlsx' || extension === 'xls') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
            resolve({});
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsBinaryString(file);
      });
    } else {
      return Promise.resolve({});
    }
  };

  const handleFileUpload = async (vendorId: string, files: File[] | FileList) => {
    // Convert FileList to array if needed (for backward compatibility with drag & drop)
    const fileArray = Array.isArray(files) ? files : Array.from(files);
    
    try {
      setUploading(prev => ({ ...prev, [vendorId]: true }));
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const validationResults = fileArray.map(file => ({
        file,
        validation: validateFile(file),
      }));

      const invalidFiles = validationResults.filter(r => !r.validation.valid);
      if (invalidFiles.length > 0) {
        invalidFiles.forEach(({ file, validation }) => {
          toast({
            title: 'Invalid file',
            description: `${file.name}: ${validation.error}`,
            variant: 'destructive',
          });
        });
      }

      const validFiles = validationResults.filter(r => r.validation.valid).map(r => r.file);
      if (validFiles.length === 0) {
        setUploading(prev => ({ ...prev, [vendorId]: false }));
        return;
      }

      const progressMap: Record<string, UploadProgress> = {};
      validFiles.forEach(file => {
        progressMap[file.name] = {
          file,
          progress: 0,
          status: 'uploading',
        };
      });
      setUploadProgress(prev => ({
        ...prev,
        [vendorId]: { ...prev[vendorId], ...progressMap },
      }));

      const uploadPromises = validFiles.map(async (file) => {
        try {
          const filePath = `vendor-invoices/${companyId}/${vendorId}/${Date.now()}-${file.name}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false,
            });

          if (uploadError) throw uploadError;

          setUploadProgress(prev => ({
            ...prev,
            [vendorId]: {
              ...prev[vendorId],
              [file.name]: {
                file,
                progress: 100,
                status: 'completed',
              },
            },
          }));

          let parsedInvoiceData: { invoiceNumber?: string; invoiceDate?: string; totalAmount?: number; lineItems?: any[] } = {};
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          
          if (fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls') {
            try {
              parsedInvoiceData = await parseInvoiceFile(file);
            } catch (parseError: any) {
              console.warn('Error parsing invoice file:', parseError);
            }
          }

          const { data: invoiceData, error: invoiceError } = await supabase
            .from('vendor_invoice_training')
            .insert({
              company_id: companyId,
              vendor_id: vendorId,
              invoice_file_url: filePath,
              invoice_file_name: file.name,
              ocr_status: fileExtension === 'csv' || fileExtension === 'xlsx' || fileExtension === 'xls' ? 'completed' : 'pending',
              invoice_number: parsedInvoiceData.invoiceNumber || null,
              invoice_date: parsedInvoiceData.invoiceDate ? new Date(parsedInvoiceData.invoiceDate).toISOString().split('T')[0] : null,
              total_amount: parsedInvoiceData.totalAmount || null,
              line_items: parsedInvoiceData.lineItems || null,
            })
            .select()
            .single();

          if (invoiceError) throw invoiceError;

          await updateInvoiceCounter(vendorId, companyId);
          return invoiceData;
        } catch (error: any) {
          setUploadProgress(prev => ({
            ...prev,
            [vendorId]: {
              ...prev[vendorId],
              [file.name]: {
                file,
                progress: 0,
                status: 'error',
                error: error.message || 'Upload failed',
              },
            },
          }));
          throw error;
        }
      });

      await Promise.all(uploadPromises);
      await loadInvoices();

      setTimeout(() => {
        setUploadProgress(prev => {
          const updated = { ...prev };
          if (updated[vendorId]) {
            delete updated[vendorId];
          }
          return updated;
        });
      }, 2000);

      toast({
        title: 'Success',
        description: `Uploaded ${validFiles.length} invoice(s)`,
      });

      // Auto-switch to processing tab if files were uploaded
      if (validFiles.length > 0) {
        setActiveTab('processing');
      }
    } catch (error: any) {
      console.error('Error uploading invoices:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload invoices',
        variant: 'destructive',
      });
    } finally {
      setUploading(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  const updateInvoiceCounter = async (vendorId: string, companyId: string) => {
    try {
      const { data: invoices } = await supabase
        .from('vendor_invoice_training')
        .select('id', { count: 'exact', head: false })
        .eq('company_id', companyId)
        .eq('vendor_id', vendorId);

      const count = invoices?.length || 0;

      await supabase
        .from('vendor_training_progress')
        .update({
          invoices_uploaded: count,
          status: count >= 5 ? 'uploading' : 'uploading',
        })
        .eq('company_id', companyId)
        .eq('vendor_id', vendorId);
    } catch (error) {
      console.error('Error updating invoice counter:', error);
    }
  };

  const handleDeleteInvoice = async (vendorId: string, invoiceId: string) => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const { data: invoice } = await supabase
        .from('vendor_invoice_training')
        .select('invoice_file_url')
        .eq('id', invoiceId)
        .single();

      if (invoice?.invoice_file_url) {
        await supabase.storage
          .from('company-documents')
          .remove([invoice.invoice_file_url]);
      }

      const { error } = await supabase
        .from('vendor_invoice_training')
        .delete()
        .eq('id', invoiceId);

      if (error) throw error;

      await updateInvoiceCounter(vendorId, companyId);
      await loadInvoices();
      // Refresh processing status to update the Processing tab
      await checkProcessingStatus();
      // Reset processing complete state since invoices changed
      setAllProcessingComplete(false);
      setProcessingStats(null);
      toast({
        title: 'Success',
        description: 'Invoice deleted',
      });
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive',
      });
    }
  };

  // Invoice correction functions (from Step 4)
  const loadTrainingSamples = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId || !currentVendorId) return;

      const { data, error } = await supabase
        .from('vendor_invoice_training_samples')
        .select('invoice_id')
        .eq('company_id', companyId)
        .eq('vendor_id', currentVendorId);

      if (error) throw error;
      setTrainingSamples(data || []);
    } catch (error) {
      console.error('Error loading training samples:', error);
    }
  };

  const loadParsingTemplate = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId || !currentVendorId) return;

      if (currentVendorInvoices.length > 0) {
        const fileType = getFileType(currentVendorInvoices[0].invoice_file_name);
        const template = await getParsingTemplate(companyId, currentVendorId, fileType);
        setParsingTemplate(template);
      }
    } catch (error) {
      console.error('Error loading parsing template:', error);
    }
  };

  const getFileType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'heic', 'gif', 'webp'].includes(ext || '')) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'csv') return 'csv';
    if (['xlsx', 'xls'].includes(ext || '')) return 'excel';
    return 'pdf';
  };

  const hasTrainingSample = (invoiceId: string) => {
    return trainingSamples.some(s => s.invoice_id === invoiceId);
  };

  const handleOpenEditor = async (invoice: VendorInvoice) => {
    let invoiceWithPreview = { ...invoice };
    
    if (!invoice.previewUrl) {
      const fileType = getFileType(invoice.invoice_file_name);
      if (fileType === 'image' || fileType === 'pdf') {
        try {
          const { data: signedUrlData } = await supabase.storage
            .from('company-documents')
            .createSignedUrl(invoice.invoice_file_url, 300);
          invoiceWithPreview.previewUrl = signedUrlData?.signedUrl || null;
        } catch (e) {
          console.warn('Error getting preview URL:', e);
        }
      }
    }
    
    try {
      const companyId = await getCurrentUserCompanyId();
      if (companyId) {
        const { data: sample } = await supabase
          .from('vendor_invoice_training_samples')
          .select('*')
          .eq('invoice_id', invoice.id)
          .single();

        if (sample) {
          invoiceWithPreview = {
            ...invoiceWithPreview,
            line_items: sample.corrected_line_items || invoice.line_items,
            total_amount: sample.corrected_total_amount || invoice.total_amount,
            invoice_date: sample.corrected_invoice_date || invoice.invoice_date,
            invoice_number: sample.corrected_invoice_number || invoice.invoice_number,
          };
        }
      }
    } catch (error) {
      console.warn('Error loading corrected data:', error);
    }
    
    setSelectedInvoice(invoiceWithPreview);
    setIsEditorOpen(true);
  };

  const handleSaveCorrections = async (correctedData: InvoiceData) => {
    if (!selectedInvoice) return;

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const lineItems = correctedData.line_items.map((item, idx) => ({
        id: item.id || `item-${idx}`,
        name: item.description,
        description: item.description,
        quantity: item.quantity,
        qty: item.quantity,
        unit: item.unit,
        price: item.unit_price,
        unitPrice: item.unit_price,
        total: item.total,
        totalPrice: item.total,
      }));

      await saveTrainingSample(
        companyId,
        currentVendorId,
        selectedInvoice.id,
        {
          ...correctedData,
          line_items: correctedData.line_items,
        },
        selectedInvoice.ocr_raw_data || {}
      );

      const { error: updateError } = await supabase
        .from('vendor_invoice_training')
        .update({
          line_items: lineItems,
          total_amount: correctedData.total_amount,
          invoice_date: correctedData.invoice_date,
          invoice_number: correctedData.invoice_number,
        })
        .eq('id', selectedInvoice.id);
      
      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Corrections saved successfully',
      });

      setIsEditorOpen(false);
      await loadTrainingSamples();
      await loadInvoices();
      await loadParsingTemplate();

      // Check if we have enough samples to generate template
      const samples = await getTrainingSamples(companyId, currentVendorId);
      if (samples.length >= 2 && !parsingTemplate) {
        toast({
          title: 'Template Ready',
          description: 'You have enough samples to generate a parsing template. Go to the Template tab.',
        });
      }
    } catch (error: any) {
      console.error('Error saving corrections:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save corrections',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateTemplate = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const samples = await getTrainingSamples(companyId, currentVendorId);
      if (samples.length < 2) {
        toast({
          title: 'Not Enough Samples',
          description: 'You need at least 2 corrected invoices to generate a template.',
          variant: 'destructive',
        });
        return;
      }

      const fileType = currentVendorInvoices.length > 0 ? getFileType(currentVendorInvoices[0].invoice_file_name) : 'pdf';
      const template = await generateParsingTemplate(companyId, currentVendorId, fileType, samples);
      const templateId = await saveParsingTemplate(template);

      setParsingTemplate({ ...template, id: templateId });
      toast({
        title: 'Success',
        description: 'Parsing template generated successfully',
      });

      setReviewSubTab('template');
    } catch (error: any) {
      console.error('Error generating template:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate template',
        variant: 'destructive',
      });
    }
  };

  const handleTestTemplate = async () => {
    if (!parsingTemplate) return;

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const testInvoices = currentVendorInvoices
        .filter(inv => inv.ocr_status === 'completed')
        .filter(inv => !hasTrainingSample(inv.id));

      const results = await Promise.all(
        testInvoices.slice(0, 5).map(async (invoice) => {
          try {
            const result = await testTemplate(parsingTemplate, invoice.id);
            return {
              invoice_id: invoice.id,
              invoice_name: invoice.invoice_file_name,
              ...result,
            };
          } catch (error) {
            return {
              invoice_id: invoice.id,
              invoice_name: invoice.invoice_file_name,
              accuracy: 0,
              errors: ['Test failed'],
              extracted_data: {},
            };
          }
        })
      );

      setTemplateTestResults(results);
      setReviewSubTab('testing');

      const avgAccuracy = results.reduce((sum, r) => sum + r.accuracy, 0) / results.length;
      toast({
        title: 'Testing Complete',
        description: `Average accuracy: ${(avgAccuracy * 100).toFixed(1)}%`,
      });
    } catch (error: any) {
      console.error('Error testing template:', error);
      toast({
        title: 'Error',
        description: 'Failed to test template',
        variant: 'destructive',
      });
    }
  };

  const correctedCount = trainingSamples.length;
  const totalInvoices = currentVendorInvoices.filter(inv => inv.ocr_status === 'completed').length;
  const progressPercentage = totalInvoices > 0 ? (correctedCount / totalInvoices) * 100 : 0;
  const vendorProgress = progress[currentVendorId] || { processed: 0, total: 0, clusters: 0, failed: 0 };
  const allProcessed = hasCheckedInitialStatus && vendorProgress.total > 0 && 
    (vendorProgress.processed + vendorProgress.failed) === vendorProgress.total;

  if (!currentVendorId) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No vendors selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Upload & Process Invoices</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Upload invoices, let AI extract data automatically, then review and correct the results.
        </p>
        
        {/* Detailed Info Section */}
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li><strong>Upload:</strong> Upload at least 5 invoices per vendor (PDF, images, CSV, or Excel files)</li>
                <li><strong>Processing:</strong> Our AI automatically extracts invoice data including line items, prices, and dates</li>
                <li><strong>Review:</strong> Review and correct any OCR errors, then generate parsing templates for future invoices</li>
              </ol>
              <p className="text-xs text-muted-foreground mt-2">
                💡 <strong>Tip:</strong> The more invoices you upload, the better our AI becomes at recognizing your vendor's invoice format.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>

      {/* Vendor Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {currentVendor?.name || `Vendor ${currentVendorIndex + 1}`}
              </CardTitle>
              <CardDescription>
                Vendor {currentVendorIndex + 1} of {selectedVendors.length} • 
                {invoiceCount} invoices uploaded
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">
                {correctedCount} / {totalInvoices} corrected
              </div>
              {totalInvoices > 0 && <Progress value={progressPercentage} className="w-32 mt-2" />}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs for Upload, Processing, and Correction */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload ({invoiceCount})
          </TabsTrigger>
          <TabsTrigger value="processing">
            <Loader2 className={`h-4 w-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
            Processing ({vendorProgress.processed}/{vendorProgress.total})
          </TabsTrigger>
          <TabsTrigger value="correction" disabled={!allProcessed || totalInvoices === 0}>
            <FileText className="h-4 w-4 mr-2" />
            Review ({correctedCount}/{totalInvoices})
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Upload Your Vendor Invoices</p>
                <p className="text-sm text-muted-foreground">
                  Upload at least 5 invoices from this vendor. Supported formats: PDF, JPG, PNG, HEIC, CSV, and Excel files.
                  You can upload multiple files at once by selecting them or dragging and dropping.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  📄 <strong>Why 5 invoices?</strong> We need multiple examples to learn your vendor's invoice format and accurately extract data from future invoices.
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {!hasEnoughInvoices ? (
            <div
              key={`upload-zone-${currentVendorId}`}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                isDragging[currentVendorId]
                  ? 'border-primary bg-primary/5 scale-[1.02]'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            onClick={(e) => {
              e.stopPropagation();
              
              // Programmatically trigger file input click
              const input = fileInputRefs.current[`upload-${currentVendorId}`];
              
              if (input) {
                input.click();
              } else {
                // Fallback: try using getElementById if ref isn't set yet
                const inputById = document.getElementById(`upload-${currentVendorId}`) as HTMLInputElement;
                if (inputById) {
                  inputById.click();
                }
              }
            }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(prev => ({ ...prev, [currentVendorId]: true }));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(prev => ({ ...prev, [currentVendorId]: false }));
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(prev => ({ ...prev, [currentVendorId]: false }));
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  // Copy files immediately to prevent stale reference
                  const filesCopy = Array.from(files);
                  handleFileUpload(currentVendorId, filesCopy);
                }
              }}
            >
              <input
                key={`upload-input-${currentVendorId}`}
                ref={(el) => {
                  fileInputRefs.current[`upload-${currentVendorId}`] = el;
                }}
                type="file"
                id={`upload-${currentVendorId}`}
                className="sr-only"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.csv,.xlsx,.xls"
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    // IMPORTANT: Copy files immediately before async operations
                    // FileList is a live reference that becomes stale when component re-renders
                    const filesCopy = Array.from(files);
                    handleFileUpload(currentVendorId, filesCopy);
                  }
                  // Clear the input after a delay
                  setTimeout(() => {
                    if (e.target) {
                      e.target.value = '';
                    }
                  }, 100);
                }}
              />
              <div 
                className="cursor-pointer block"
              >
                <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging[currentVendorId] ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className="text-sm font-medium mb-1">
                  Drag & drop invoices here or click to upload
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF, JPG, PNG, HEIC, CSV, Excel (max {MAX_FILE_SIZE / 1024 / 1024}MB per file)
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Upload at least 5 invoices for best results
                </p>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-green-200 rounded-lg p-6 text-center bg-green-50/50">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <p className="text-sm font-medium text-green-700">
                All invoices uploaded for this vendor
              </p>
              <p className="text-xs text-green-600 mt-1">
                You can upload more invoices below if needed
              </p>
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress[currentVendorId] && Object.keys(uploadProgress[currentVendorId]).length > 0 && (
            <div className="space-y-2">
              {Object.entries(uploadProgress[currentVendorId]).map(([fileName, progress]) => (
                <div key={fileName} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="truncate flex-1">{fileName}</span>
                    <span className="ml-2">
                      {progress.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      {progress.status === 'error' && <X className="h-4 w-4 text-red-600" />}
                      {progress.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin" />}
                    </span>
                  </div>
                  {progress.status === 'uploading' && (
                    <Progress value={progress.progress} className="h-1" />
                  )}
                  {progress.status === 'error' && progress.error && (
                    <p className="text-xs text-red-600">{progress.error}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Uploaded invoices list */}
          {currentVendorInvoices.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Uploaded Invoices ({currentVendorInvoices.length}):</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {currentVendorInvoices.map((invoice) => {
                  const isImage = invoice.invoice_file_name.match(/\.(jpg|jpeg|png|heic)$/i);
                  const previewUrl = invoice.previewUrl || (isImage ? supabase.storage.from('company-documents').getPublicUrl(invoice.invoice_file_url).data.publicUrl : null);
                  
                  return (
                    <div
                      key={invoice.id}
                      className="flex items-start gap-3 p-3 bg-muted rounded-lg border"
                    >
                      <div className="flex-1 min-w-0">
                        {previewUrl && isImage ? (
                          <img
                            src={previewUrl}
                            alt={invoice.invoice_file_name}
                            className="w-full h-32 object-cover rounded border mb-2"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="mb-2 flex items-center justify-center w-full h-32 bg-background rounded border">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="space-y-1">
                          <p className="text-sm font-medium truncate" title={invoice.invoice_file_name}>
                            {invoice.invoice_file_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {invoice.ocr_status || 'pending'}
                            </Badge>
                            {invoice.total_amount && (
                              <span>${invoice.total_amount.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteInvoice(currentVendorId, invoice.id)}
                        title="Delete file"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Processing Tab */}
        <TabsContent value="processing" className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">AI Processing Your Invoices</p>
                <p className="text-sm text-muted-foreground">
                  Our AI is automatically extracting data from your uploaded invoices. This includes invoice numbers, dates, 
                  line items, quantities, prices, and totals. Processing typically takes 1-3 minutes per invoice.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  ⚡ <strong>Automatic:</strong> Processing starts automatically when you upload invoices. You can continue working while it processes.
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {!hasCheckedInitialStatus ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Checking invoice status...</p>
                </div>
              </CardContent>
            </Card>
          ) : processing || vendorProgress.total > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {processing && <Loader2 className="h-5 w-5 animate-spin" />}
                  Processing Invoices
                </CardTitle>
                <CardDescription>
                  Automatically extracting data from your invoices. This may take a few minutes...
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Vendor Summary */}
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{vendorNames[currentVendorId] || currentVendor?.name || 'Vendor'}</span>
                      {vendorProgress.failed === 0 && vendorProgress.processed > 0 && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      {vendorProgress.failed > 0 && <AlertCircle className="h-4 w-4 text-red-600" />}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Total: </span>
                        <span className="font-medium">{vendorProgress.total}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Processed: </span>
                        <span className="font-medium text-green-600">{vendorProgress.processed}</span>
                      </div>
                      {vendorProgress.failed > 0 && (
                        <div>
                          <span className="text-muted-foreground">Failed: </span>
                          <span className="font-medium text-red-600">{vendorProgress.failed}</span>
                        </div>
                      )}
                      <TooltipProvider>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Clusters: </span>
                          <span className="font-medium">{vendorProgress.clusters}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-semibold mb-1">What are Clusters?</p>
                              <p className="text-xs">
                                Clusters are groups of similar line items found across your invoices 
                                (e.g., "Chicken Breast" appearing on multiple invoices gets grouped together). 
                                This helps map products to your menu items.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </div>
                    {vendorProgress.processed > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span>Processing Progress</span>
                          <span>{Math.round((vendorProgress.processed / vendorProgress.total) * 100)}%</span>
                        </div>
                        <Progress value={(vendorProgress.processed / vendorProgress.total) * 100} className="h-2" />
                      </div>
                    )}
                  </div>

                  {/* Individual Invoice List */}
                  {currentVendorInvoices.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-2 border-b">
                        <h4 className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Processed Invoices ({currentVendorInvoices.length})
                        </h4>
                      </div>
                      <div className="divide-y">
                        {currentVendorInvoices.map((invoice) => {
                          const lineItemCount = Array.isArray(invoice.line_items) ? invoice.line_items.length : 0;
                          const isCompleted = invoice.ocr_status === 'completed';
                          const isFailed = invoice.ocr_status === 'failed';
                          
                          return (
                            <div
                              key={invoice.id}
                              className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 cursor-pointer transition-colors"
                              onClick={() => {
                                setDetailInvoice(invoice);
                                setIsDetailModalOpen(true);
                              }}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium truncate">{invoice.invoice_file_name}</p>
                                  <Badge 
                                    variant={isCompleted ? 'default' : isFailed ? 'destructive' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {invoice.ocr_status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                  {invoice.invoice_date && (
                                    <span>Date: {new Date(invoice.invoice_date).toLocaleDateString()}</span>
                                  )}
                                  {invoice.total_amount != null && (
                                    <span className="font-medium text-foreground">${invoice.total_amount.toFixed(2)}</span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Layers className="h-3 w-3" />
                                    {lineItemCount} line items
                                  </span>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm" className="ml-2">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Failed invoices alert */}
                  {vendorProgress.failed > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-1">
                          <p className="font-medium">Failed invoices:</p>
                          <ul className="list-disc list-inside text-xs">
                            {(failedInvoices[currentVendorId] || []).slice(0, 3).map((name, idx) => (
                              <li key={idx}>{name}</li>
                            ))}
                            {(failedInvoices[currentVendorId] || []).length > 3 && (
                              <li>...and {(failedInvoices[currentVendorId] || []).length - 3} more</li>
                            )}
                          </ul>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={async () => {
                              const companyId = await getCurrentUserCompanyId();
                              if (!companyId) return;
                              const { data: failedInvs } = await supabase
                                .from('vendor_invoice_training')
                                .select('id')
                                .eq('company_id', companyId)
                                .eq('vendor_id', currentVendorId)
                                .eq('ocr_status', 'failed');
                              if (failedInvs && failedInvs.length > 0) {
                                await supabase
                                  .from('vendor_invoice_training')
                                  .update({ ocr_status: 'pending' })
                                  .in('id', failedInvs.map(inv => inv.id));
                                await handleProcessInvoices();
                              }
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Retry Failed
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No invoices to process. Upload invoices in the Upload tab first.
              </AlertDescription>
            </Alert>
          )}

          {allProcessed && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium">Processing Complete</p>
                <p className="text-sm mt-1">
                  {vendorProgress.clusters} clusters identified. Switch to the Review tab to correct invoice data.
                </p>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Correction Tab */}
        <TabsContent value="correction" className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Review & Correct Invoice Data</p>
                <p className="text-sm text-muted-foreground">
                  Review the AI-extracted data and correct any errors. After correcting at least 2 invoices, 
                  you can generate a parsing template that will improve accuracy for future invoices from this vendor.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  ✏️ <strong>Why correct?</strong> Your corrections help train the AI to better understand this vendor's invoice format, 
                  making future processing more accurate.
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {totalInvoices === 0 ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No processed invoices found. Complete processing first.
              </AlertDescription>
            </Alert>
          ) : (
            <Tabs value={reviewSubTab} onValueChange={(v) => setReviewSubTab(v as any)}>
              <TabsList>
                <TabsTrigger value="invoices">
                  <FileText className="h-4 w-4 mr-2" />
                  Invoices ({totalInvoices})
                </TabsTrigger>
                <TabsTrigger value="template" disabled={!parsingTemplate && correctedCount < 2}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Template {parsingTemplate && <CheckCircle2 className="h-3 w-3 ml-1 text-green-600" />}
                </TabsTrigger>
                <TabsTrigger value="testing" disabled={!parsingTemplate}>
                  <TestTube className="h-4 w-4 mr-2" />
                  Testing
                </TabsTrigger>
              </TabsList>

              <TabsContent value="invoices" className="space-y-4">
                <Alert variant="outline">
                  <FileText className="h-4 w-4" />
                  <AlertDescription>
                    <p className="text-sm">
                      Click on any invoice to review and correct the extracted data. Make sure line items, prices, and totals are accurate.
                    </p>
                  </AlertDescription>
                </Alert>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentVendorInvoices
                    .filter(inv => inv.ocr_status === 'completed')
                    .map((invoice) => {
                      const isCorrected = hasTrainingSample(invoice.id);
                      const lineItemCount = Array.isArray(invoice.line_items) ? invoice.line_items.length : 0;

                      return (
                        <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm truncate">{invoice.invoice_file_name}</CardTitle>
                              {isCorrected && (
                                <Badge variant="default" className="bg-green-600">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Corrected
                                </Badge>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="text-xs text-muted-foreground space-y-1">
                              {invoice.invoice_date && (
                                <p>Date: {new Date(invoice.invoice_date).toLocaleDateString()}</p>
                              )}
                              {invoice.total_amount != null && (
                                <p className="font-medium text-foreground">Total: ${invoice.total_amount.toFixed(2)}</p>
                              )}
                              <p>{lineItemCount} line items</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => handleOpenEditor(invoice)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {isCorrected ? 'Review' : 'Correct'}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>

                {correctedCount >= 2 && !parsingTemplate && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                      <span>You have enough corrected invoices to generate a parsing template.</span>
                      <Button size="sm" onClick={handleGenerateTemplate}>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Template
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </TabsContent>

              <TabsContent value="template" className="space-y-4">
                <Alert variant="outline">
                  <Sparkles className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">Parsing Template</p>
                      <p className="text-sm text-muted-foreground">
                        A parsing template is a set of rules generated from your corrections that helps the AI 
                        extract data more accurately from future invoices. Edit the template to fine-tune how data is extracted.
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>

                {parsingTemplate ? (
                  <ParsingTemplateBuilder
                    template={parsingTemplate}
                    onUpdate={(updated) => setParsingTemplate(updated)}
                    onTest={handleTestTemplate}
                  />
                ) : (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      {correctedCount < 2
                        ? 'Correct at least 2 invoices to generate a parsing template.'
                        : 'Click "Generate Template" to create a parsing template from your corrections.'}
                    </AlertDescription>
                  </Alert>
                )}
              </TabsContent>

              <TabsContent value="testing" className="space-y-4">
                <Alert variant="outline">
                  <TestTube className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">Test Your Parsing Template</p>
                      <p className="text-sm text-muted-foreground">
                        Test your parsing template against invoices that haven't been corrected yet. 
                        This shows you how accurate the template is and helps identify areas for improvement.
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>

                {templateTestResults.length > 0 ? (
                  <div className="space-y-4">
                    {templateTestResults.map((result) => (
                      <Card key={result.invoice_id}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">{result.invoice_name}</CardTitle>
                            <Badge variant={result.accuracy > 0.8 ? 'default' : result.accuracy > 0.5 ? 'secondary' : 'destructive'}>
                              {(result.accuracy * 100).toFixed(1)}% accurate
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {result.errors.length > 0 && (
                            <div className="space-y-1 mb-3">
                              {result.errors.map((error: string, idx: number) => (
                                <p key={idx} className="text-xs text-destructive">{error}</p>
                              ))}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            Extracted: {JSON.stringify(result.extracted_data, null, 2)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      {parsingTemplate
                        ? 'Click "Test Template" to validate the template against remaining invoices.'
                        : 'Generate a parsing template first to enable testing.'}
                    </AlertDescription>
                  </Alert>
                )}
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>
      </Tabs>

      {/* Summary Display after Processing */}
      {allProcessingComplete && (
        <>
          {loadingStats ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-7 w-64" />
                <Skeleton className="h-4 w-96" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <Skeleton className="h-20 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card>
                <CardContent className="pt-6">
                  <Skeleton className="h-40 w-full" />
                </CardContent>
              </Card>
            </div>
          ) : processingStats && processingStats.totalInvoices > 0 ? (
            <>
              <VendorInvoiceSummaryDisplay stats={processingStats} showGuidelines={true} />
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={onNext} size="lg">
                  Start Mapping Clusters
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <Alert className="border-amber-500 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>No invoices processed.</strong> Processing completed but no invoices were successfully processed. Please check your invoice files and try again.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Invoice Detail Modal for OCR Results */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Details
            </DialogTitle>
            <DialogDescription>
              OCR extracted data from {detailInvoice?.invoice_file_name}
            </DialogDescription>
          </DialogHeader>
          
          {detailInvoice && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-6">
                {/* Invoice Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge variant={detailInvoice.ocr_status === 'completed' ? 'default' : 'destructive'}>
                      {detailInvoice.ocr_status}
                    </Badge>
                  </div>
                  {detailInvoice.invoice_number && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Invoice #</p>
                      <p className="text-sm font-medium">{detailInvoice.invoice_number}</p>
                    </div>
                  )}
                  {detailInvoice.invoice_date && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Invoice Date</p>
                      <p className="text-sm font-medium">
                        {new Date(detailInvoice.invoice_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {detailInvoice.total_amount != null && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Total Amount</p>
                      <p className="text-sm font-semibold text-green-600">
                        ${detailInvoice.total_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  {(() => {
                    const summaryKeywords = ['sub-total', 'subtotal', 'tax', 'total', 'cash', 'balance', 'previous balance', 'payment', 'discount', 'shipping', 'fee'];
                    const isSummaryItem = (item: any) => {
                      const desc = (item.name || item.description || '').toLowerCase().trim();
                      return summaryKeywords.some(keyword => desc === keyword || desc.includes(keyword));
                    };

                    const actualLineItems = detailInvoice.line_items?.filter((item: any) => !isSummaryItem(item)) || [];
                    const summaryItems = detailInvoice.line_items?.filter((item: any) => isSummaryItem(item)) || [];

                    return (
                      <>
                        <h4 className="text-sm font-semibold flex items-center gap-2">
                          <Layers className="h-4 w-4" />
                          Extracted Line Items ({actualLineItems.length})
                        </h4>
                        
                        {(!detailInvoice.line_items || detailInvoice.line_items.length === 0) ? (
                          <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              <p className="font-medium">No line items extracted</p>
                              <p className="text-xs mt-1">
                                The OCR couldn't extract individual line items from this invoice. 
                                This could happen if the invoice format is unusual or the image quality is low.
                              </p>
                            </AlertDescription>
                          </Alert>
                        ) : (
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[40%]">Description</TableHead>
                                  <TableHead className="text-right">Qty</TableHead>
                                  <TableHead className="text-right">Unit</TableHead>
                                  <TableHead className="text-right">Price</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {actualLineItems.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                      No line items found (only summary items detected)
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  actualLineItems.map((item: any, index: number) => (
                                    <TableRow key={index}>
                                      <TableCell className="font-medium">
                                        {item.name || item.description || 'N/A'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.quantity ?? item.qty ?? '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.unit || '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.price != null || item.unit_price != null 
                                          ? (() => {
                                              const price = item.price ?? item.unit_price ?? 0;
                                              return price < 0 
                                                ? `-$${Math.abs(price).toFixed(2)}` 
                                                : `$${price.toFixed(2)}`;
                                            })()
                                          : '-'}
                                      </TableCell>
                                      <TableCell className={`text-right font-medium ${(item.total ?? item.line_total ?? 0) < 0 ? 'text-red-600' : ''}`}>
                                        {item.total != null || item.line_total != null
                                          ? (() => {
                                              const total = item.total ?? item.line_total ?? 0;
                                              return total < 0 
                                                ? `-$${Math.abs(total).toFixed(2)}` 
                                                : `$${total.toFixed(2)}`;
                                            })()
                                          : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        )}

                        {/* Order Summary Section */}
                        {summaryItems.length > 0 && (
                          <div className="space-y-2 mt-4">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              Order Summary
                            </h4>
                            <div className="border rounded-lg overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[40%]">Description</TableHead>
                                    <TableHead className="text-right">Qty</TableHead>
                                    <TableHead className="text-right">Unit</TableHead>
                                    <TableHead className="text-right">Price</TableHead>
                                    <TableHead className="text-right">Total</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {summaryItems.map((item: any, index: number) => (
                                    <TableRow key={index}>
                                      <TableCell className="font-medium">
                                        {item.name || item.description || 'N/A'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.quantity ?? item.qty ?? '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.unit || '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.price != null || item.unit_price != null 
                                          ? (() => {
                                              const price = item.price ?? item.unit_price ?? 0;
                                              return price < 0 
                                                ? `-$${Math.abs(price).toFixed(2)}` 
                                                : `$${price.toFixed(2)}`;
                                            })()
                                          : '-'}
                                      </TableCell>
                                      <TableCell className={`text-right font-medium ${(item.total ?? item.line_total ?? 0) < 0 ? 'text-red-600' : ''}`}>
                                        {item.total != null || item.line_total != null
                                          ? (() => {
                                              const total = item.total ?? item.line_total ?? 0;
                                              return total < 0 
                                                ? `-$${Math.abs(total).toFixed(2)}` 
                                                : `$${total.toFixed(2)}`;
                                            })()
                                          : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Additional Info */}
                <div className="pt-4 border-t text-xs text-muted-foreground space-y-1">
                  <p><span className="font-medium">File:</span> {detailInvoice.invoice_file_name}</p>
                  <p><span className="font-medium">Vendor:</span> {vendorNames[detailInvoice.vendor_id] || detailInvoice.vendor_id}</p>
                  {detailInvoice.created_at && (
                    <p><span className="font-medium">Uploaded:</span> {new Date(detailInvoice.created_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] w-[95vw]">
          <DialogHeader>
            <DialogTitle>Correct Invoice Data</DialogTitle>
            <DialogDescription>
              Review and correct the extracted data from {selectedInvoice?.invoice_file_name}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[85vh]">
            {selectedInvoice && (
              <InvoiceFieldEditor
                invoiceData={{
                  line_items: (selectedInvoice.line_items || []).map((item: any, idx: number) => ({
                    id: item.id || `item-${idx}`,
                    description: item.name || item.description || '',
                    quantity: item.quantity || item.qty || 1,
                    unit: item.unit || '',
                    unit_price: item.price !== undefined && item.price !== null ? item.price : (item.unitPrice !== undefined && item.unitPrice !== null ? item.unitPrice : 0),
                    total: item.total !== undefined && item.total !== null ? item.total : (item.totalPrice !== undefined && item.totalPrice !== null ? item.totalPrice : 0),
                    is_discount: item.is_discount || false,
                  })),
                  total_amount: selectedInvoice.total_amount || 0,
                  invoice_date: selectedInvoice.invoice_date || undefined,
                  invoice_number: selectedInvoice.invoice_number || undefined,
                }}
                onSave={handleSaveCorrections}
                onCancel={() => setIsEditorOpen(false)}
                previewUrl={selectedInvoice.previewUrl}
                fileName={selectedInvoice.invoice_file_name}
                fileType={getFileType(selectedInvoice.invoice_file_name) as any}
                fileUrl={selectedInvoice.invoice_file_url}
                taggunRawData={selectedInvoice.ocr_raw_data}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Navigation */}
      {selectedVendors.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (currentVendorIndex > 0) {
                    onVendorIndexChange(currentVendorIndex - 1);
                  }
                }}
                disabled={currentVendorIndex === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous Vendor
              </Button>
              <span className="text-sm text-muted-foreground">
                Vendor {currentVendorIndex + 1} of {selectedVendors.length}
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (currentVendorIndex < selectedVendors.length - 1) {
                    onVendorIndexChange(currentVendorIndex + 1);
                  }
                }}
                disabled={currentVendorIndex >= selectedVendors.length - 1}
              >
                Next Vendor
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={onNext} variant="outline">
          Next Step
        </Button>
      </div>
    </div>
  );
};

